import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect, lazy, Suspense } from 'react';
import { useAuth } from './hooks/useAuth';
import { usePermissions } from './hooks/usePermissions';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PermissionSetup } from './components/PermissionSetup';
import { Login } from './pages/Login';
import { Signup } from './pages/Signup';
import { ParentHome } from './pages/ParentHome';
import { GpsSender } from './pages/GpsSender';
import { AppShell } from './components/layout/AppShell';
import { SOSAlertMonitor } from './components/SOSAlertMonitor';
import { ToastContainer } from './components/Toast';
import { App as CapApp } from '@capacitor/app';
import { subscribeToPush } from './hooks/usePushNotifications';

// Eager admin pages (commonly used)
import { Dashboard } from './pages/Dashboard';
import { LiveStatus } from './components/admin/LiveStatus';
import { BusManager } from './components/admin/BusManager';
import { RouteManager } from './components/admin/RouteManager';
import { StudentManager } from './components/admin/StudentManager';
import { UserManager } from './components/admin/UserManager';

// Eager role-specific entry points
import { DriverPortal } from './pages/DriverPortal';
import { ParentPortal } from './pages/ParentPortal';

// Lazy-loaded admin pages (code-split for smaller initial bundle)
const FleetManagement = lazy(() => import('./pages/FleetManagement').then(m => ({ default: m.FleetManagement })));
const SafetyAlerts = lazy(() => import('./pages/SafetyAlerts').then(m => ({ default: m.SafetyAlerts })));
const TripsPage = lazy(() => import('./pages/TripsPage').then(m => ({ default: m.TripsPage })));
const ChatPage = lazy(() => import('./pages/ChatPage').then(m => ({ default: m.ChatPage })));
const ReportsPage = lazy(() => import('./pages/ReportsPage').then(m => ({ default: m.ReportsPage })));
const BillingPage = lazy(() => import('./pages/BillingPage').then(m => ({ default: m.BillingPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const AuditPage = lazy(() => import('./pages/AuditPage').then(m => ({ default: m.AuditPage })));
const SuperAdminPage = lazy(() => import('./pages/SuperAdminPage').then(m => ({ default: m.SuperAdminPage })) as any);
const FeedbackPage = lazy(() => import('./pages/FeedbackPage').then(m => ({ default: m.FeedbackPage })));
const Replay = lazy(() => import('./pages/Replay').then(m => ({ default: m.Replay })));
const OrgDashboard = lazy(() => import('./pages/OrgDashboard').then(m => ({ default: m.OrgDashboard })) as any);
const LicenseManagementPage = lazy(() => import('./pages/LicenseManagementPage').then(m => ({ default: m.LicenseManagementPage })));

function AdminApp({ onLogout, userName }: { onLogout: () => void; userName: string }) {
  const [activePage, setActivePage] = useState('dashboard');

  const renderPage = () => {
    switch (activePage) {
      case 'dashboard': return <Dashboard onNavigate={setActivePage} />;
      case 'live': return <LiveStatus />;
      case 'fleet': return <FleetManagement onNavigate={setActivePage} />;
      case 'buses': return <BusManager />;
      case 'routes': return <RouteManager />;
      case 'students': return <StudentManager />;
      case 'users': return <UserManager />;
      case 'trips': return <TripsPage />;
      case 'safety': return <SafetyAlerts />;
      case 'chat': return <ChatPage />;
      case 'reports': return <ReportsPage />;
      case 'feedback': return <FeedbackPage />;
      case 'billing': return <BillingPage />;
      case 'settings': return <SettingsPage />;
      case 'audit': return <AuditPage />;
      case 'licenses': return <LicenseManagementPage />;
      case 'superadmin': return <SuperAdminPage onNavigate={setActivePage} />;
      case 'organization': return <OrgDashboard onNavigate={setActivePage} />;
      case 'replay': return <Replay onBack={() => setActivePage('dashboard')} />;
      default: return <Dashboard />;
    }
  };

  // Replay takes full screen
  if (activePage === 'replay') {
    return (
      <Suspense fallback={<div className="admin-loading">Loading...</div>}>
        <Replay onBack={() => setActivePage('dashboard')} />
      </Suspense>
    );
  }

  return (
    <>
      <AppShell
        activePage={activePage}
        onNavigate={setActivePage}
        userName={userName}
        onLogout={onLogout}
      >
        <ErrorBoundary key={activePage}>
          <Suspense fallback={<div className="admin-loading">Loading...</div>}>
            {renderPage()}
          </Suspense>
        </ErrorBoundary>
      </AppShell>
      <SOSAlertMonitor onNavigate={setActivePage} />
    </>
  );
}

export default function App() {
  const {
    user, isLoggedIn, loading, error, login, logout,
    loginWithBiometric, enableBiometricLogin, dismissBiometricPrompt,
    showBiometricPrompt, biometricAvailable, biometricEnabled, biometricType,
  } = useAuth();

  const [permissionsDone, setPermissionsDone] = useState(() => {
    return localStorage.getItem('permissions_setup_done') === 'true';
  });

  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Android back button handler
  useEffect(() => {
    const listener = CapApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        CapApp.exitApp();
      }
    });
    return () => { listener.then(l => l.remove()); };
  }, []);

  // Online/offline detection
  useEffect(() => {
    const goOnline = () => setIsOffline(false);
    const goOffline = () => setIsOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // Auto-subscribe admin/staff to push notifications (they skip PermissionSetup)
  useEffect(() => {
    if (!isLoggedIn || !user) return;
    const roles = user.roles || [user.role];
    const isAdminRole = roles.some(r => ['super_admin', 'school_admin', 'transport_coordinator', 'fleet_manager', 'staff'].includes(r));
    if (isAdminRole && 'Notification' in window && Notification.permission === 'default') {
      // Ask for notification permission + subscribe
      Notification.requestPermission().then(perm => {
        if (perm === 'granted') subscribeToPush();
      });
    } else if (isAdminRole && Notification.permission === 'granted') {
      // Already granted but might not be subscribed — subscribe silently
      subscribeToPush();
    }
  }, [isLoggedIn, user]);

  if (!isLoggedIn) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/signup" element={<Signup />} />
          <Route path="*" element={
            <Login
              onLogin={login}
              onBiometricLogin={loginWithBiometric}
              onEnableBiometric={enableBiometricLogin}
              onDismissBiometricPrompt={dismissBiometricPrompt}
              showBiometricPrompt={showBiometricPrompt}
              biometricAvailable={biometricAvailable}
              biometricEnabled={biometricEnabled}
              biometricType={biometricType}
              error={error}
              loading={loading}
            />
          } />
        </Routes>
      </BrowserRouter>
    );
  }

  const roles = user!.roles || [user!.role];
  const isDriver = roles.includes('driver') && !roles.some(r => ['super_admin', 'school_admin', 'transport_coordinator'].includes(r));
  const isParent = roles.includes('parent') && !roles.some(r => ['super_admin', 'school_admin', 'transport_coordinator'].includes(r));
  const isStudent = roles.includes('student') && roles.length === 1;
  const isSecurityGuard = roles.includes('security_guard') && !roles.some(r => ['super_admin', 'school_admin'].includes(r));

  // Show permission setup on first launch for driver and parent roles
  if (!permissionsDone && (isDriver || isParent)) {
    return (
      <PermissionSetup
        isDriver={isDriver}
        isParent={isParent}
        onComplete={() => setPermissionsDone(true)}
      />
    );
  }

  return (
    <BrowserRouter>
      {isOffline && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          background: '#ff6b35', color: '#fff', textAlign: 'center',
          padding: '8px 16px', fontSize: '14px', fontWeight: 600,
        }}>
          You are offline. Some features may not work.
        </div>
      )}
      <div style={isOffline ? { paddingTop: '36px' } : undefined}>
        <Routes>
          {/* Driver → Driver Portal (GPS + Trip Sheet + Attendance) */}
          {isDriver && (
            <Route path="*" element={<DriverPortal onLogout={logout} userName={user!.name} />} />
          )}

          {/* Parent → Parent Portal (Map + Trip History + Feedback) */}
          {isParent && (
            <>
              <Route path="/" element={<ParentPortal onLogout={logout} userName={user!.name} />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}

          {/* Student → Simple Map View */}
          {isStudent && (
            <>
              <Route path="/" element={<ParentHome onLogout={logout} userName={user!.name} />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}

          {/* Admin / Staff / Transport / Fleet → Full Admin Portal with Sidebar */}
          {!isDriver && !isParent && !isStudent && (
            <>
              <Route path="/" element={<AdminApp onLogout={logout} userName={user!.name} />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      </div>
      <ToastContainer />
    </BrowserRouter>
  );
}
